import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { Icons } from '@ohif/ui-next';
import VirtualizedDropdown, { type VirtualizedDropdownOption } from '../common/VirtualizedDropdown';
import type { HubAssignmentSelectionState } from '../../../routes/WorkList/worklistSelectionUtils';
import type { ImagingWorklistLayout } from '../../utils/roleUtils';
import {
  IMAGING_COLUMN_LABELS,
  type HubSpokeWorklistColumnKey,
  type ImagingColumnKey,
} from '../../constants/imagingWorklistColumns';

export type ImagingListFilters = {
  reportStatus: string;
  priority: string;
  studyScope: string;
  /** Selected radiologist user id for RIS `assigned_id` (empty = all). */
  radiologistId: string;
  /** AI Result filter value for RIS `ai_result` (`all` = no filter). */
  aiResult: string;
};

export type ImagingRadiologistOption = { id: number; fullName: string };

function formatToolbarDateDisplay(iso: string): string {
  if (!iso || iso.length < 10) {
    return '';
  }
  const [year, month, day] = iso.split('-');
  if (!year || !month || !day) {
    return iso;
  }
  return `${day}/${month}/${year}`;
}

function ImagingWorklistDateField({
  'aria-label': ariaLabel,
  placeholder,
  value,
  onChange,
}: Readonly<{
  'aria-label': string;
  placeholder: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const displayText =
    value.length >= 10 ? formatToolbarDateDisplay(value.slice(0, 10)) : placeholder;

  return (
    <div className="imaging-worklist-date-field">
      <input
        ref={inputRef}
        type="date"
        className="imaging-worklist-date-field__input"
        aria-label={ariaLabel}
        value={value}
        onChange={onChange}
        onClick={() => {
          const dateInput = inputRef.current;
          if (!dateInput || typeof dateInput.showPicker !== 'function') {
            return;
          }
          try {
            /* showPicker() may return undefined (not a Promise) in some Chromium builds — avoid .catch on undefined */
            void Promise.resolve(dateInput.showPicker()).catch(() => {
              /* Rejected e.g. without user gesture; native control may still open */
            });
          } catch {
            /* InvalidStateError or other sync failure */
          }
        }}
      />
      <div
        className="imaging-worklist-date-field__face"
        aria-hidden
      >
        <span className="imaging-worklist-date-field__label">{displayText}</span>
        <Calendar
          className="imaging-worklist-date-field__calendar-icon"
          strokeWidth={1.5}
          aria-hidden
        />
      </div>
    </div>
  );
}

export type ImagingWorklistToolbarProps = {
  layout: ImagingWorklistLayout;
  filterValues: {
    patientName: string;
    studyDate: { startDate: string | null; endDate: string | null };
    modalities: string[];
  };
  /** Merged into WorkList filter state */
  onMergeFilterValues: (patch: Record<string, unknown>) => void;
  imagingListFilters: ImagingListFilters;
  onImagingListFiltersChange: (next: ImagingListFilters) => void;
  modalityOptions: { value: string; label: string }[];
  radiologistOptions: ImagingRadiologistOption[];
  showRadiologistFilter: boolean;
  showAssignActions: boolean;
  showAssignHubAction?: boolean;
  hasCheckedStudies: boolean;
  checkedStudiesCount: number;
  hasAssignedStudies: boolean;
  hubAssignmentSelectionState?: HubAssignmentSelectionState | null;
  onAssignClick: () => void;
  onAssignHubClick?: () => void;
  onUnassignClick: () => void;
  onUnassignHubClick?: () => void;
  /** When selection is all Approved reports — show Assign Peer Review action. */
  showPeerReviewAssignAction?: boolean;
  onPeerReviewAssignClick?: () => void;
  columnVisibility: Record<ImagingColumnKey, boolean>;
  onColumnVisibilityChange: (next: Record<ImagingColumnKey, boolean>) => void;
  showAssignedColumn: boolean;
  hubSpokeColumnKey?: HubSpokeWorklistColumnKey | null;
};

export const REPORT_OPTIONS = [
  'all',
  'Pending',
  'Drafted',
  'Awaiting Review',
  'Rejected',
  'Approved',
];
export const PRIORITY_OPTIONS = ['all', 'ROUTINE', 'URGENT', 'VIP', 'MLC'];
export const STUDY_SCOPE_OPTIONS = ['all', 'Assigned', 'Unassigned'];
/** Values sent as `ai_result` query param (except `all`). */
export const AI_RESULT_OPTIONS = [
  'all',
  'normal',
  'abnormal',
  'pending',
  'processing',
  'failed',
  'timeout',
  'none',
];

/** DICOM / filter YYYYMMDD ↔ input[type=date] yyyy-MM-dd */
function studyDateToInputValue(ymd: string | null | undefined): string {
  if (!ymd || ymd.length < 8) {
    return '';
  }
  const digitsYyyymmdd = ymd.replace(/\D/g, '').slice(0, 8);
  if (digitsYyyymmdd.length !== 8) {
    return '';
  }
  return `${digitsYyyymmdd.slice(0, 4)}-${digitsYyyymmdd.slice(4, 6)}-${digitsYyyymmdd.slice(6, 8)}`;
}

function inputValueToStudyDate(iso: string): string | null {
  if (!iso || iso.length < 10) {
    return null;
  }
  return iso.replace(/-/g, '').slice(0, 8);
}

function localYyyymmdd(offsetDays = 0): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + offsetDays);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}

function labelForReportOption(statusFilterValue: string) {
  if (statusFilterValue === 'all') {
    return 'All Status';
  }
  return statusFilterValue;
}

function labelForPriorityOption(priorityFilterValue: string) {
  if (priorityFilterValue === 'all') {
    return 'All Priorities';
  }
  if (priorityFilterValue === 'ROUTINE') {
    return 'Routine';
  }
  if (priorityFilterValue === 'URGENT') {
    return 'Urgent';
  }
  if (priorityFilterValue === 'VIP') {
    return 'VIP';
  }
  if (priorityFilterValue === 'MLC') {
    return 'MLC';
  }
  return priorityFilterValue;
}

function labelForStudyScopeOption(studyScopeFilterValue: string) {
  if (studyScopeFilterValue === 'all') {
    return 'All Studies';
  }
  return studyScopeFilterValue;
}

function labelForAiResultOption(aiResultFilterValue: string) {
  if (aiResultFilterValue === 'all') {
    return 'All AI results';
  }
  if (aiResultFilterValue === 'normal') {
    return 'Normal';
  }
  if (aiResultFilterValue === 'abnormal') {
    return 'Abnormal';
  }
  if (aiResultFilterValue === 'pending') {
    return 'Pending';
  }
  if (aiResultFilterValue === 'processing') {
    return 'Processing';
  }
  if (aiResultFilterValue === 'failed') {
    return 'Failed';
  }
  if (aiResultFilterValue === 'timeout') {
    return 'Timeout';
  }
  if (aiResultFilterValue === 'none') {
    return 'Not Processed';
  }
  return aiResultFilterValue;
}

export default function ImagingWorklistToolbar({
  layout,
  filterValues,
  onMergeFilterValues,
  imagingListFilters,
  onImagingListFiltersChange,
  modalityOptions,
  radiologistOptions,
  showRadiologistFilter,
  showAssignActions,
  showAssignHubAction = false,
  hasCheckedStudies,
  checkedStudiesCount,
  hasAssignedStudies,
  hubAssignmentSelectionState = null,
  onAssignClick,
  onAssignHubClick,
  onUnassignClick,
  onUnassignHubClick,
  showPeerReviewAssignAction = false,
  onPeerReviewAssignClick,
  columnVisibility,
  onColumnVisibilityChange,
  showAssignedColumn,
  hubSpokeColumnKey = null,
}: Readonly<ImagingWorklistToolbarProps>) {
  const [columnsOpen, setColumnsOpen] = useState(false);
  const colRef = useRef<HTMLDivElement>(null);

  const fromInputValue = studyDateToInputValue(filterValues.studyDate?.startDate ?? null);
  const toInputValue = studyDateToInputValue(filterValues.studyDate?.endDate ?? null);

  useEffect(() => {
    const closeColumnsOnOutsideClick = (event: MouseEvent) => {
      if (colRef.current && !colRef.current.contains(event.target as Node)) {
        setColumnsOpen(false);
      }
    };
    if (columnsOpen) {
      document.addEventListener('mousedown', closeColumnsOnOutsideClick);
    }
    return () => document.removeEventListener('mousedown', closeColumnsOnOutsideClick);
  }, [columnsOpen]);

  const modalitySelectValue = useMemo(() => {
    if (!filterValues.modalities?.length) {
      return '';
    }
    return filterValues.modalities[0];
  }, [filterValues.modalities]);

  const toggleableColumns: ImagingColumnKey[] = useMemo(() => {
    const keys: ImagingColumnKey[] = [
      'uhid',
      'patientName',
      'age',
      'modality',
      'studyDescription',
      'studyDateTime',
      'tests',
      'clinicalHistory',
      'appointmentType',
      'priority',
      'reportStatus',
      'reportDateTime',
      'aiResult',
    ];
    if (layout === 'adminTech' && showAssignedColumn) {
      keys.push('assigned');
    }
    if (layout === 'adminTech' && hubSpokeColumnKey) {
      keys.push(hubSpokeColumnKey);
    }
    return keys;
  }, [layout, showAssignedColumn, hubSpokeColumnKey]);

  const onModalityChange = (modalityCode: string) => {
    onMergeFilterValues({
      modalities: modalityCode ? [modalityCode] : [],
      pageNumber: 1,
    });
  };

  const reportStatusDropdownOptions: VirtualizedDropdownOption[] = useMemo(
    () =>
      REPORT_OPTIONS.map((reportStatus, index) => ({
        id: index + 1,
        label: labelForReportOption(reportStatus),
      })),
    []
  );
  const reportStatusDropdownValue = useMemo((): number | null => {
    const statusIndex = REPORT_OPTIONS.indexOf(imagingListFilters.reportStatus);
    return statusIndex >= 0 ? statusIndex + 1 : 1;
  }, [imagingListFilters.reportStatus]);

  const handleReportStatusChange = (optionId: number | null, _label: string) => {
    if (optionId == null) {
      return;
    }
    const reportStatus = REPORT_OPTIONS[optionId - 1];
    if (reportStatus) {
      onImagingListFiltersChange({
        ...imagingListFilters,
        reportStatus,
      });
    }
  };

  const studyScopeDropdownOptions: VirtualizedDropdownOption[] = useMemo(
    () =>
      STUDY_SCOPE_OPTIONS.map((studyScope, index) => ({
        id: index + 1,
        label: labelForStudyScopeOption(studyScope),
      })),
    []
  );
  const studyScopeDropdownValue = useMemo((): number | null => {
    const scopeIndex = STUDY_SCOPE_OPTIONS.indexOf(imagingListFilters.studyScope);
    return scopeIndex >= 0 ? scopeIndex + 1 : 1;
  }, [imagingListFilters.studyScope]);

  const handleStudyScopeChange = (optionId: number | null, _label: string) => {
    if (optionId == null) {
      return;
    }
    const studyScope = STUDY_SCOPE_OPTIONS[optionId - 1];
    if (studyScope) {
      onImagingListFiltersChange({
        ...imagingListFilters,
        studyScope,
      });
    }
  };

  const priorityDropdownOptions: VirtualizedDropdownOption[] = useMemo(
    () =>
      PRIORITY_OPTIONS.map((priority, index) => ({
        id: index + 1,
        label: labelForPriorityOption(priority),
      })),
    []
  );
  const priorityDropdownValue = useMemo((): number | null => {
    const priorityIndex = PRIORITY_OPTIONS.indexOf(imagingListFilters.priority);
    return priorityIndex >= 0 ? priorityIndex + 1 : 1;
  }, [imagingListFilters.priority]);

  const handlePriorityChange = (optionId: number | null, _label: string) => {
    if (optionId == null) {
      return;
    }
    const priority = PRIORITY_OPTIONS[optionId - 1];
    if (priority) {
      onImagingListFiltersChange({
        ...imagingListFilters,
        priority,
      });
    }
  };

  const aiResultDropdownOptions: VirtualizedDropdownOption[] = useMemo(
    () =>
      AI_RESULT_OPTIONS.map((aiResult, index) => ({
        id: index + 1,
        label: labelForAiResultOption(aiResult),
      })),
    []
  );
  const aiResultDropdownValue = useMemo((): number | null => {
    const aiIndex = AI_RESULT_OPTIONS.indexOf(imagingListFilters.aiResult || 'all');
    return aiIndex >= 0 ? aiIndex + 1 : 1;
  }, [imagingListFilters.aiResult]);

  const handleAiResultChange = (optionId: number | null, _label: string) => {
    if (optionId == null) {
      return;
    }
    const aiResult = AI_RESULT_OPTIONS[optionId - 1];
    if (aiResult) {
      onImagingListFiltersChange({
        ...imagingListFilters,
        aiResult,
      });
    }
  };

  const modalityDropdownOptions: VirtualizedDropdownOption[] = useMemo(() => {
    const rows: VirtualizedDropdownOption[] = [{ id: 1, label: 'All Modalities' }];
    modalityOptions.forEach((modalityOption, index) => {
      rows.push({ id: index + 2, label: modalityOption.label });
    });
    return rows;
  }, [modalityOptions]);

  const modalityDropdownValue = useMemo((): number | null => {
    if (!modalitySelectValue) {
      return 1;
    }
    const modalityIndex = modalityOptions.findIndex(
      modalityOption => modalityOption.value === modalitySelectValue
    );
    return modalityIndex >= 0 ? modalityIndex + 2 : 1;
  }, [modalitySelectValue, modalityOptions]);

  const handleModalityFilterChange = (optionId: number | null, _label: string) => {
    if (optionId == null) {
      return;
    }
    if (optionId === 1) {
      onModalityChange('');
      return;
    }
    const selectedModalityOption = modalityOptions[optionId - 2];
    if (selectedModalityOption) {
      onModalityChange(selectedModalityOption.value);
    }
  };

  const radiologistDropdownOptions: VirtualizedDropdownOption[] = useMemo(() => {
    const rows: VirtualizedDropdownOption[] = [{ id: 1, label: 'All Radiologists' }];
    radiologistOptions.forEach((radiologistOption, index) => {
      rows.push({ id: index + 2, label: radiologistOption.fullName });
    });
    return rows;
  }, [radiologistOptions]);

  const radiologistDropdownValue = useMemo((): number | null => {
    const rid = (imagingListFilters.radiologistId || '').trim();
    if (!rid) {
      return 1;
    }
    const radiologistIndex = radiologistOptions.findIndex(
      radiologistOption => String(radiologistOption.id) === rid
    );
    return radiologistIndex >= 0 ? radiologistIndex + 2 : 1;
  }, [imagingListFilters.radiologistId, radiologistOptions]);

  const handleRadiologistChange = (optionId: number | null, _label: string) => {
    if (optionId == null) {
      return;
    }
    if (optionId === 1) {
      onImagingListFiltersChange({
        ...imagingListFilters,
        radiologistId: '',
      });
      return;
    }
    const selected = radiologistOptions[optionId - 2];
    if (selected) {
      onImagingListFiltersChange({
        ...imagingListFilters,
        radiologistId: String(selected.id),
      });
    }
  };

  const todayYmd = localYyyymmdd(0);
  const yesterdayYmd = localYyyymmdd(-1);
  const startDate = filterValues.studyDate?.startDate ?? null;
  const endDate = filterValues.studyDate?.endDate ?? null;
  const isTodayRange = Boolean(startDate && startDate === endDate && startDate === todayYmd);
  const isYesterdayRange = Boolean(
    startDate && startDate === endDate && startDate === yesterdayYmd
  );

  const applyStudyDate = (nextStartDate: string | null, nextEndDate: string | null) => {
    onMergeFilterValues({
      studyDate: { startDate: nextStartDate, endDate: nextEndDate },
      pageNumber: 1,
    });
  };

  const toggleQuickDate = (kind: 'today' | 'yesterday') => {
    if ((kind === 'today' && isTodayRange) || (kind === 'yesterday' && isYesterdayRange)) {
      applyStudyDate(null, null);
      return;
    }
    const ymd = kind === 'today' ? todayYmd : yesterdayYmd;
    applyStudyDate(ymd, ymd);
  };

  return (
    <div className="shrink-0 bg-white pt-4 pb-0 shadow-sm sm:pt-5">
      <div className="imaging-worklist-toolbar-header-row flex flex-wrap items-center gap-4 pb-4 sm:gap-6">
        <h2 className="imaging-toolbar-title shrink-0 text-xl font-bold leading-tight">
          Imaging Worklist
        </h2>
        <div className="imaging-worklist-toolbar-dates-row flex flex-wrap items-center gap-2 sm:gap-3">
          <ImagingWorklistDateField
            aria-label="Start date (From)"
            placeholder="From"
            value={fromInputValue}
            onChange={event =>
              applyStudyDate(
                inputValueToStudyDate(event.target.value),
                filterValues.studyDate?.endDate ?? null
              )
            }
          />
          <ImagingWorklistDateField
            aria-label="End date (To)"
            placeholder="To"
            value={toInputValue}
            onChange={event =>
              applyStudyDate(
                filterValues.studyDate?.startDate ?? null,
                inputValueToStudyDate(event.target.value)
              )
            }
          />
          <div
            className="imaging-worklist-date-tabs"
            role="tablist"
            aria-label="Quick date range"
          >
            <button
              type="button"
              role="tab"
              aria-selected={isTodayRange}
              className={
                isTodayRange
                  ? 'imaging-worklist-date-tabs__btn imaging-worklist-date-tabs__btn--active'
                  : 'imaging-worklist-date-tabs__btn'
              }
              onClick={() => toggleQuickDate('today')}
            >
              Today
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={isYesterdayRange}
              className={
                isYesterdayRange
                  ? 'imaging-worklist-date-tabs__btn imaging-worklist-date-tabs__btn--active'
                  : 'imaging-worklist-date-tabs__btn'
              }
              onClick={() => toggleQuickDate('yesterday')}
            >
              Yesterday
            </button>
          </div>
        </div>
      </div>
      <hr
        className="imaging-worklist-toolbar-divider"
        aria-hidden
      />

      {hasCheckedStudies && showAssignActions && (
        <div className="mb-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onAssignClick}
            className="rounded-full bg-blue-600 px-3 py-1.5 text-sm font-semibold hover:bg-blue-700"
            style={{ color: '#ffffff' }}
          >
            {hasAssignedStudies ? 'Reassign Radiologist' : 'Assign Radiologist'} (
            {checkedStudiesCount})
          </button>
          {showPeerReviewAssignAction && onPeerReviewAssignClick ? (
            <button
              type="button"
              onClick={onPeerReviewAssignClick}
              className="rounded-full border-2 border-blue-600 px-3 py-1.5 text-sm font-semibold text-blue-700 hover:bg-blue-50"
            >
              Assign Peer Review ({checkedStudiesCount})
            </button>
          ) : null}
          {showAssignHubAction && onAssignHubClick && hubAssignmentSelectionState === 'none' ? (
            <button
              type="button"
              onClick={onAssignHubClick}
              className="rounded-full border-2 border-blue-600 px-3 py-1.5 text-sm font-semibold text-blue-700 hover:bg-blue-50"
            >
              Assign to Hub Facility ({checkedStudiesCount})
            </button>
          ) : null}
          {showAssignHubAction && onAssignHubClick && hubAssignmentSelectionState === 'all' ? (
            <button
              type="button"
              onClick={onAssignHubClick}
              className="rounded-full border-2 border-blue-600 px-3 py-1.5 text-sm font-semibold text-blue-700 hover:bg-blue-50"
            >
              Reassign to Hub Facility ({checkedStudiesCount})
            </button>
          ) : null}
          {hasAssignedStudies && (
            <button
              type="button"
              onClick={onUnassignClick}
              className="rounded-full border-2 border-blue-600 px-3 py-1.5 text-sm font-semibold text-blue-700 hover:bg-blue-50"
            >
              Unassign ({checkedStudiesCount})
            </button>
          )}
          {showAssignHubAction && hubAssignmentSelectionState === 'all' && onUnassignHubClick ? (
            <button
              type="button"
              onClick={onUnassignHubClick}
              className="rounded-full border-2 border-blue-600 px-3 py-1.5 text-sm font-semibold text-blue-700 hover:bg-blue-50"
            >
              Unassign from Hub ({checkedStudiesCount})
            </button>
          ) : null}
        </div>
      )}

      <div className="imaging-worklist-toolbar-filters-row">
        <div className="imaging-worklist-toolbar-filters-row__main flex min-w-0 flex-nowrap items-center gap-2 overflow-x-visible pb-0.5 pr-[105px] [overflow-anchor:none] lg:gap-3">
          <div
            className="imaging-worklist-search"
            role="search"
          >
            <Icons.Search
              className="imaging-worklist-search__icon shrink-0"
              aria-hidden
            />
            <input
              type="search"
              className="imaging-worklist-search__input"
              placeholder="Search"
              value={filterValues.patientName}
              onChange={event =>
                onMergeFilterValues({
                  patientName: event.target.value,
                  pageNumber: 1,
                })
              }
            />
          </div>

          <div className="imaging-worklist-filter-dd-wrap">
            <VirtualizedDropdown
              ariaLabel="Report status"
              value={reportStatusDropdownValue}
              onChange={handleReportStatusChange}
              options={reportStatusDropdownOptions}
              placeholder="All Status"
              showNone={false}
              showSearch={false}
              mutedDisplayLabels={['All Status']}
              containerClassName="imaging-worklist-filter-vd-wrap w-full min-w-0"
              className="imaging-worklist-filter-vd-trigger"
              variant="light"
              onlyKeyboardSyncsListScroll
            />
          </div>

          <div className="imaging-worklist-filter-dd-wrap imaging-worklist-filter-dd-wrap--wide">
            <VirtualizedDropdown
              ariaLabel="Modality"
              value={modalityDropdownValue}
              onChange={handleModalityFilterChange}
              options={modalityDropdownOptions}
              placeholder="All Modalities"
              showNone={false}
              showSearch={false}
              mutedDisplayLabels={['All Modalities']}
              containerClassName="imaging-worklist-filter-vd-wrap w-full min-w-0"
              className="imaging-worklist-filter-vd-trigger"
              variant="light"
            />
          </div>

          <div className="imaging-worklist-filter-dd-wrap">
            <VirtualizedDropdown
              ariaLabel="Studies"
              value={studyScopeDropdownValue}
              onChange={handleStudyScopeChange}
              options={studyScopeDropdownOptions}
              placeholder="All Studies"
              showNone={false}
              showSearch={false}
              mutedDisplayLabels={['All Studies']}
              containerClassName="imaging-worklist-filter-vd-wrap w-full min-w-0"
              className="imaging-worklist-filter-vd-trigger"
              variant="light"
            />
          </div>

          <div className="imaging-worklist-filter-dd-wrap">
            <VirtualizedDropdown
              ariaLabel="Priority"
              value={priorityDropdownValue}
              onChange={handlePriorityChange}
              options={priorityDropdownOptions}
              placeholder="All Priorities"
              showNone={false}
              showSearch={false}
              mutedDisplayLabels={['All Priorities']}
              containerClassName="imaging-worklist-filter-vd-wrap w-full min-w-0"
              className="imaging-worklist-filter-vd-trigger"
              variant="light"
            />
          </div>

          <div className="imaging-worklist-filter-dd-wrap">
            <VirtualizedDropdown
              ariaLabel="AI results"
              value={aiResultDropdownValue}
              onChange={handleAiResultChange}
              options={aiResultDropdownOptions}
              placeholder="All AI results"
              showNone={false}
              showSearch={false}
              mutedDisplayLabels={['All AI results']}
              containerClassName="imaging-worklist-filter-vd-wrap w-full min-w-0"
              className="imaging-worklist-filter-vd-trigger"
              variant="light"
              onlyKeyboardSyncsListScroll
            />
          </div>

          {showRadiologistFilter && (
            <div className="imaging-worklist-filter-dd-wrap imaging-worklist-filter-dd-wrap--wide">
              <VirtualizedDropdown
                ariaLabel="Radiologist"
                value={radiologistDropdownValue}
                onChange={handleRadiologistChange}
                options={radiologistDropdownOptions}
                placeholder="All Radiologists"
                showNone={false}
                showSearch={true}
                searchPlaceholder="Search..."
                emptyMessage="No radiologists found"
                mutedDisplayLabels={['All Radiologists']}
                containerClassName="imaging-worklist-filter-vd-wrap w-full min-w-0"
                className="imaging-worklist-filter-vd-trigger"
                variant="light"
                nativeOptionsWheelScroll
                onlyKeyboardSyncsListScroll
              />
            </div>
          )}
        </div>

        <div
          className="imaging-worklist-columns-filter"
          ref={colRef}
        >
          <button
            type="button"
            onClick={() => setColumnsOpen(prevOpen => !prevOpen)}
            className="imaging-worklist-columns-filter__btn"
          >
            <Icons.Settings
              className="imaging-worklist-columns-filter__icon"
              aria-hidden
            />
            <span className="imaging-worklist-columns-filter__label">Columns</span>
          </button>
          {columnsOpen && (
            <div className="imaging-worklist-columns-filter__panel w-56 rounded-md border border-slate-200 bg-white py-2 shadow-lg">
              <div className="px-3 pb-2 text-xs font-semibold uppercase text-slate-500">
                Visible columns
              </div>
              {toggleableColumns.map(columnKey => (
                <label
                  key={columnKey}
                  className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    checked={columnVisibility[columnKey] !== false}
                    onChange={() =>
                      onColumnVisibilityChange({
                        ...columnVisibility,
                        [columnKey]: !columnVisibility[columnKey],
                      })
                    }
                  />
                  <span>{IMAGING_COLUMN_LABELS[columnKey]}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
